import { Label } from "@awtoban/ui";
import { Plus, X } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

export type GroupKind = "single" | "twin" | "triple";
export interface GroupRow {
  kind: GroupKind;
  weight: string;
}

export const KIND_WHEELS: Record<GroupKind, number> = { single: 1, twin: 2, triple: 3 };

// Side-view layout, in px. The SVG is fixed-scale and the whole panel scrolls
// horizontally, so the weight pills below stay aligned to their axle groups.
const PAD = 28;
const CAB_W = 116;
const COUPLING = 8;
const WHEEL_R = 16;
const RIM_R = 9;
const HUB_R = 3.5;
const AXLE_GAP = 42; // wheel center-to-center within a group
const CONTROL_W = 100; // weight pill + remove button
const MIN_SLOT = CONTROL_W + 14; // each group reserves at least this width → controls never overlap
const WHEEL_CY = 126;
const BODY_TOP = 32;
const BODY_BOTTOM = 104;
const CAT_Y = 162;
const ART_H = 176;
const EDITOR_TOP = 184; // weight-pill band, below the art
const CONTAINER_H = 230;

interface GroupLayout {
  i: number;
  kind: GroupKind;
  xs: number[];
  center: number;
  x0: number;
  x1: number;
}

function layout(groups: GroupRow[]) {
  const frontX = PAD + 42;
  const cabEnd = PAD + CAB_W;
  const trailerX0 = cabEnd + COUPLING;
  // Lay each group in its own slot, at least as wide as the weight control, so
  // adjacent controls (and wheels) can never overlap — even several singles.
  let x = trailerX0 + 12; // left edge of the first slot
  const out: GroupLayout[] = groups.map((g, i) => {
    const n = KIND_WHEELS[g.kind];
    const wheelSpan = (n - 1) * AXLE_GAP + 2 * WHEEL_R;
    const slotW = Math.max(wheelSpan, MIN_SLOT);
    const center = x + slotW / 2;
    const startWheel = center - ((n - 1) * AXLE_GAP) / 2;
    const xs: number[] = [];
    for (let k = 0; k < n; k++) {
      xs.push(startWheel + k * AXLE_GAP);
    }
    x += slotW;
    return {
      i,
      kind: g.kind,
      xs,
      center,
      x0: center - wheelSpan / 2,
      x1: center + wheelSpan / 2,
    };
  });
  const trailerX1 = (out.length > 0 ? x : trailerX0 + 96) + 8;
  const width = trailerX1 + PAD;
  return { frontX, cabEnd, trailerX0, trailerX1, width, groups: out };
}

function Wheel({ x }: { x: number }) {
  return (
    <g>
      <ellipse cx={x} cy={WHEEL_CY + WHEEL_R} rx={WHEEL_R} ry={3.5} fill="#0f172a" opacity={0.12} />
      <circle cx={x} cy={WHEEL_CY} r={WHEEL_R} fill="#111827" />
      <circle cx={x} cy={WHEEL_CY} r={RIM_R} fill="#e2e8f0" />
      <circle cx={x} cy={WHEEL_CY} r={HUB_R} fill="#64748b" />
    </g>
  );
}

function AddButton({ kind, onClick }: { kind: GroupKind; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-2 rounded-full border border-line-strong bg-paper px-3 py-1.5 font-medium text-sm shadow-sm transition-colors hover:border-ink hover:bg-paper-soft"
    >
      <Plus className="h-3.5 w-3.5 text-ink-muted" />
      <span className="flex items-center gap-1">
        {Array.from({ length: KIND_WHEELS[kind] }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed wheel count
          <span key={i} className="inline-block h-2.5 w-2.5 rounded-full bg-ink" />
        ))}
      </span>
      {t(`compare.group_kinds.${kind}`)}
    </button>
  );
}

export function TruckBuilder({
  front,
  onFront,
  groups,
  onAdd,
  onRemove,
  onWeight,
}: {
  front: string;
  onFront: (v: string) => void;
  groups: GroupRow[];
  onAdd: (kind: GroupKind) => void;
  onRemove: (i: number) => void;
  onWeight: (i: number, v: string) => void;
}) {
  const { t } = useTranslation();
  const L = React.useMemo(() => layout(groups), [groups]);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Label className="mr-1">{t("compare.build_truck")}</Label>
        <AddButton kind="single" onClick={() => onAdd("single")} />
        <AddButton kind="twin" onClick={() => onAdd("twin")} />
        <AddButton kind="triple" onClick={() => onAdd("triple")} />
      </div>

      <div className="overflow-x-auto rounded-xl border border-line-strong bg-gradient-to-b from-paper to-paper-soft px-4 pt-3 pb-2">
        <div className="relative" style={{ width: L.width, height: CONTAINER_H }}>
          <svg width={L.width} height={ART_H} role="img" aria-label={t("compare.build_truck")}>
            <defs>
              <linearGradient id="tb-cab" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#64748b" />
                <stop offset="1" stopColor="#334155" />
              </linearGradient>
              <linearGradient id="tb-trailer" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#fbfdff" />
                <stop offset="1" stopColor="#dbe3ec" />
              </linearGradient>
            </defs>

            {/* road */}
            <line
              x1={0}
              y1={WHEEL_CY + WHEEL_R + 3}
              x2={L.width}
              y2={WHEEL_CY + WHEEL_R + 3}
              stroke="#cbd5e1"
              strokeWidth={2}
              strokeLinecap="round"
            />

            {/* frame rail */}
            <rect
              x={PAD + 8}
              y={WHEEL_CY - WHEEL_R - 4}
              width={L.trailerX1 - (PAD + 8)}
              height={6}
              rx={3}
              fill="#1f2937"
            />

            {/* trailer box */}
            <rect
              x={L.trailerX0}
              y={BODY_TOP}
              width={Math.max(L.trailerX1 - L.trailerX0, 0)}
              height={BODY_BOTTOM - BODY_TOP}
              rx={9}
              fill="url(#tb-trailer)"
              stroke="#94a3b8"
            />
            {/* container ribs */}
            {L.groups.length > 0
              ? [0.28, 0.5, 0.72].map((f) => {
                  const x = L.trailerX0 + (L.trailerX1 - L.trailerX0) * f;
                  return (
                    <line
                      key={f}
                      x1={x}
                      y1={BODY_TOP + 8}
                      x2={x}
                      y2={BODY_BOTTOM - 8}
                      stroke="#cbd5e1"
                    />
                  );
                })
              : null}

            {/* cab */}
            <rect
              x={PAD}
              y={BODY_TOP + 6}
              width={CAB_W}
              height={BODY_BOTTOM - BODY_TOP - 6}
              rx={12}
              fill="url(#tb-cab)"
            />
            {/* windshield */}
            <path
              d={`M ${PAD + 8} ${BODY_TOP + 16} Q ${PAD + 8} ${BODY_TOP + 12} ${PAD + 13} ${BODY_TOP + 12} L ${PAD + 42} ${BODY_TOP + 12} L ${PAD + 42} ${BODY_TOP + 34} L ${PAD + 8} ${BODY_TOP + 34} Z`}
              fill="#bfdbfe"
            />
            {/* headlight */}
            <rect x={PAD + 4} y={BODY_BOTTOM - 22} width={6} height={11} rx={2} fill="#fde68a" />

            {/* front (steering) wheel + caption */}
            <Wheel x={L.frontX} />
            <text
              x={L.frontX}
              y={CAT_Y}
              textAnchor="middle"
              fontSize={10}
              fontWeight={500}
              fill="#94a3b8"
            >
              {t("compare.front")}
            </text>

            {/* groups */}
            {L.groups.map((g) => (
              <g key={g.i}>
                <rect
                  x={g.x0 - 5}
                  y={WHEEL_CY - WHEEL_R - 7}
                  width={g.x1 - g.x0 + 10}
                  height={WHEEL_R * 2 + 14}
                  rx={10}
                  fill="#0f172a"
                  opacity={0.04}
                />
                {g.xs.map((x) => (
                  <Wheel key={x} x={x} />
                ))}
                <text
                  x={g.center}
                  y={CAT_Y}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="#475569"
                >
                  {t(`compare.group_kinds.${g.kind}`)}
                </text>
              </g>
            ))}
          </svg>

          {/* front weight pill (excluded) */}
          <div
            className="absolute flex flex-col items-center gap-0.5"
            style={{ left: L.frontX, top: EDITOR_TOP, transform: "translateX(-50%)" }}
          >
            <WeightPill value={front} onChange={onFront} muted />
            <span className="text-[9px] text-ink-muted uppercase tracking-wide">
              {t("compare.excluded")}
            </span>
          </div>

          {/* per-group weight pill + remove */}
          {L.groups.map((g) => (
            <div
              key={g.i}
              className="absolute flex items-center gap-1"
              style={{ left: g.center, top: EDITOR_TOP, transform: "translateX(-50%)" }}
            >
              <WeightPill
                value={groups[g.i]?.weight ?? ""}
                onChange={(v) => onWeight(g.i, v)}
                label={t("compare.weight")}
              />
              <button
                type="button"
                aria-label={t("compare.remove")}
                className="grid h-6 w-6 place-items-center rounded-full text-ink-muted transition-colors hover:bg-danger/10 hover:text-danger"
                onClick={() => onRemove(g.i)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
      {groups.length === 0 ? (
        <p className="mt-2 text-[12px] text-ink-muted">{t("compare.build_hint")}</p>
      ) : null}
    </div>
  );
}

function WeightPill({
  value,
  onChange,
  label,
  muted,
}: {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  muted?: boolean;
}) {
  return (
    <label
      className={`flex w-[78px] cursor-text items-center gap-0.5 overflow-hidden rounded-full border bg-paper py-1 pr-2.5 pl-3 shadow-sm transition-[box-shadow,border-color] focus-within:border-ink focus-within:ring-2 focus-within:ring-ink/15 ${
        muted ? "border-line" : "border-line-strong"
      }`}
    >
      <input
        inputMode="decimal"
        aria-label={label}
        placeholder="0"
        className="w-full min-w-0 appearance-none bg-transparent text-center text-sm tabular-nums outline-none focus:outline-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="shrink-0 text-[11px] text-ink-muted">t</span>
    </label>
  );
}
